import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api/client';
import { useUIStore } from '../../../stores/uiStore';
import { Loader2, Search, Star } from 'lucide-react';

function DiscoverSubTab({
  maxAsins = 50,
  sites,
  filters: mainFilters,
  category: propCategory,
  quickMode,
  onCurate,
  condition = 'new',
}: {
  maxAsins?: number;
  sites?: string[];
  filters?: any;
  category?: any;
  quickMode?: boolean;
  onCurate?: (asins: string[]) => void;
  condition?: 'new' | 'refurbished';
}) {
  const { t } = useTranslation();
  const { sellerId } = useUIStore();
  const [catId, setCatId] = useState('');
  const [catName, setCatName] = useState('');
  const [asins, setAsins] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [tokens, setTokens] = useState<number | null>(null);
  const [discovered, setDiscovered] = useState(false);
  const [info, setInfo] = useState<any>(null);
  const prevRef = useRef<any>(null);
  const [catSearch, setCatSearch] = useState('');
  const [catSearchResults, setCatSearchResults] = useState<any[] | null>(null);
  const [catSearchLoading, setCatSearchLoading] = useState(false);
  const [enqueuing, setEnqueuing] = useState(false);
  const [keywordSearch, setKeywordSearch] = useState('');
  const [searchError, setSearchError] = useState<string | null>(null);

  // Keepa responde con 'keepa_rate_limit' cuando la cuenta se queda sin
  // tokens — sin este aviso la pantalla quedaba vacía (incidente 2026-07-09)
  const searchErrorMsg = (resError?: string) =>
    resError === 'keepa_rate_limit' ? t('tendencias.keepaQuota') : (resError || 'Search failed');

  // Sync category from Categories sub-tab
  useEffect(() => {
    if (propCategory && propCategory !== prevRef.current) {
      prevRef.current = propCategory;
      setCatId(String(propCategory.catId || propCategory.id || propCategory.category_id || ''));
      setCatName(propCategory.name || propCategory.category_name || '');
      setDiscovered(false);
      setAsins([]);
      setSelected(new Set());
      setInfo(null);
    }
  }, [propCategory]);

  // Category search
  const searchCategories = async () => {
    if (!catSearch.trim()) return;
    setCatSearchLoading(true);
    try {
      const res = await api.get<any>(`/publisher/discover/categories/roots?seller_id=${sellerId}&term=${encodeURIComponent(catSearch.trim())}`);
      // Fallback: try search endpoint
      if (res.success && res.data) {
        const cats = Array.isArray(res.data) ? res.data : res.data.categories || [];
        setCatSearchResults(cats.filter((c: any) =>
          (c.name || '').toLowerCase().includes(catSearch.trim().toLowerCase())
        ).slice(0, 20));
      }
    } catch {
      setCatSearchResults([]);
    } finally {
      setCatSearchLoading(false);
    }
  };

  const selectSearchCat = (cat: any) => {
    setCatId(String(cat.catId || cat.id || cat.category_id || ''));
    setCatName(cat.name || cat.category_name || '');
    setCatSearchResults(null);
    setCatSearch('');
    setDiscovered(false);
    setAsins([]);
    setSelected(new Set());
    setInfo(null);
  };

  // Detect if advanced filters are active (V1 parity: line 4915)
  const hasAdvFilters = (() => {
    const f = mainFilters || {};
    return !!(
      (f.adv_title || '').trim() || (f.adv_material || '').trim() ||
      (f.adv_brand || []).length || (f.adv_manufacturer || []).length ||
      (f.adv_color || []).length || (f.adv_size || []).length ||
      (f.adv_languages || []).length || (f.adv_binding || []).length ||
      (f.adv_author || []).length || (f.adv_publisher || []).length ||
      (f.adv_genre || []).length ||
      f.adv_numberOfPages_gte || f.adv_numberOfPages_lte ||
      f.adv_numberOfItems_gte || f.adv_numberOfItems_lte ||
      (f.adv_productType !== undefined && f.adv_productType !== '') ||
      f.adv_hasReviews === true || f.adv_isSNS === true ||
      f.adv_noHazMat === true || f.adv_hasAPlus === true || f.adv_isDeal === true ||
      (f.adv_categories_include || []).length || (f.adv_categories_exclude || []).length
    );
  })();

  // Build Keepa Product Finder selection (V1 parity: dashboard.html:4928-4970)
  const buildSelection = (rootCatId: number) => {
    const f = mainFilters || {};
    const en = f._enabled || {};
    const ck = (k: string) => en[k] !== false;
    const ptVal = f.adv_productType !== undefined && f.adv_productType !== '' ? Number(f.adv_productType) : 0;
    const sel: any = { productType: [ptVal], rootCategory: rootCatId, sort: [['current_SALES', 'asc']], perPage: maxAsins, page: 0 };

    if (ck('current_SALES_lte') && f.current_SALES_lte) sel.current_SALES_lte = parseInt(f.current_SALES_lte);
    // Price filter: use REFURBISHED keys when condition is refurbished
    if (f.itemCondition === 'refurbished') {
      if (ck('current_NEW_gte') && f.current_NEW_gte) sel.current_REFURBISHED_gte = parseInt(f.current_NEW_gte);
      if (ck('current_NEW_lte') && f.current_NEW_lte) sel.current_REFURBISHED_lte = parseInt(f.current_NEW_lte);
    } else {
      if (ck('current_NEW_gte') && f.current_NEW_gte) sel.current_NEW_gte = parseInt(f.current_NEW_gte);
      if (ck('current_NEW_lte') && f.current_NEW_lte) sel.current_NEW_lte = parseInt(f.current_NEW_lte);
    }
    if (ck('current_NEW_FBA_gte') && f.current_NEW_FBA_gte >= 1) sel.current_NEW_FBA_gte = parseInt(f.current_NEW_FBA_gte);
    if (ck('avg90_SALES_lte') && f.avg90_SALES_lte) sel.avg90_SALES_lte = parseInt(f.avg90_SALES_lte);
    if (ck('minRating') && f.minRating) sel.minRating = parseInt(f.minRating);
    if (f.isAdultProduct === false) sel.isAdultProduct = false;
    if (ck('monthlySold_gte') && f.monthlySold_gte) sel.monthlySold_gte = parseInt(f.monthlySold_gte);
    if (ck('packageWeight_lte') && f.packageWeight_lte) sel.packageWeight_lte = parseInt(f.packageWeight_lte);
    if (ck('packageMaxSide_lte') && f.packageMaxSide_lte) { const mm = parseInt(f.packageMaxSide_lte); sel.packageLength_lte = mm; sel.packageWidth_lte = mm; sel.packageHeight_lte = mm; }
    if (ck('salesRankDrops30_gte') && f.salesRankDrops30_gte) sel.salesRankDrops30_gte = parseInt(f.salesRankDrops30_gte);
    if (ck('offerCountFBA_lte') && f.offerCountFBA_lte) sel.offerCountFBA_lte = parseInt(f.offerCountFBA_lte);
    if (ck('outOfStockPct90Amz_gte') && f.outOfStockPct90Amz_gte) sel.outOfStockPercentage90_AMAZON_gte = parseInt(f.outOfStockPct90Amz_gte);
    if (f.excludeAmazonSeller) sel.buyBoxIsAmazon = false;
    if (f.excludePreorders) sel.isPreorder = false;

    // Advanced Keepa filters
    if ((f.adv_title || '').trim()) sel.title = f.adv_title.trim();
    if ((f.adv_material || '').trim()) sel.material = f.adv_material.trim();
    if ((f.adv_brand || []).length > 0) sel.brand = f.adv_brand;
    if ((f.adv_manufacturer || []).length > 0) sel.manufacturer = f.adv_manufacturer;
    if ((f.adv_color || []).length > 0) sel.color = f.adv_color;
    if ((f.adv_size || []).length > 0) sel.size = f.adv_size;
    if ((f.adv_languages || []).length > 0) sel.languages = f.adv_languages;
    if ((f.adv_binding || []).length > 0) sel.binding = f.adv_binding;
    if ((f.adv_author || []).length > 0) sel.author = f.adv_author;
    if ((f.adv_publisher || []).length > 0) sel.publisher = f.adv_publisher;
    if ((f.adv_genre || []).length > 0) sel.genre = f.adv_genre;
    if (f.adv_numberOfPages_gte) sel.numberOfPages_gte = parseInt(f.adv_numberOfPages_gte);
    if (f.adv_numberOfPages_lte) sel.numberOfPages_lte = parseInt(f.adv_numberOfPages_lte);
    if (f.adv_numberOfItems_gte) sel.numberOfItems_gte = parseInt(f.adv_numberOfItems_gte);
    if (f.adv_numberOfItems_lte) sel.numberOfItems_lte = parseInt(f.adv_numberOfItems_lte);
    if (f.adv_hasReviews === true) sel.hasReviews = true;
    if (f.adv_isSNS === true) sel.isSNS = true;
    if (f.adv_noHazMat === true) sel.isHazMat = false;
    if (f.adv_hasAPlus === true) sel.hasAPlus = true;
    if (f.adv_isDeal === true) sel.isDeal = true;
    if ((f.adv_categories_include || []).length > 0) sel.categories_include = f.adv_categories_include;
    if ((f.adv_categories_exclude || []).length > 0) sel.categories_exclude = f.adv_categories_exclude;

    return sel;
  };

  // Discover bestsellers (no filters applied)
  const discoverBestsellers = async () => {
    if (!catId) return;
    setLoading(true);
    setSearchError(null);
    try {
      const res = await api.post<any>('/publisher/discover/bestsellers', {
        category: catId,
        seller_id: sellerId,
        limit: maxAsins,
      });
      if (res.success && res.data) {
        const data = res.data;
        const newAsins = data.asins || data.new_asins || [];
        setAsins(newAsins);
        setSelected(new Set(newAsins));
        setTokens(data.tokens_left);
        setDiscovered(true);
        setInfo({
          total: newAsins.length,
          new: (data.new_asins || newAsins).length,
          dups: (data.duplicates || []).length,
        });
      } else {
        setSearchError(searchErrorMsg(res.error));
      }
    } catch (err) {
      console.error('Bestsellers discover failed:', err);
      setSearchError(searchErrorMsg());
    } finally {
      setLoading(false);
    }
  };

  // Discover filtered (Keepa Product Finder with all filters)
  const discoverFiltered = async () => {
    if (!catId) return;
    setLoading(true);
    setSearchError(null);
    try {
      const selection = buildSelection(parseInt(catId));
      const res = await api.post<any>('/publisher/discover/finder', {
        selection,
        seller_id: sellerId,
      });
      if (res.success && res.data) {
        const data = res.data;
        const newAsins = data.asins || data.new_asins || [];
        setAsins(newAsins);
        setSelected(new Set(newAsins));
        setTokens(data.tokens_left);
        setDiscovered(true);
        setInfo({
          total: newAsins.length,
          new: (data.new_asins || newAsins).length,
          dups: (data.duplicates || []).length,
          totalMatches: data.total_results,
          filtered: true,
        });
      } else {
        setSearchError(searchErrorMsg(res.error));
      }
    } catch (err) {
      console.error('Product finder search failed:', err);
      setSearchError(searchErrorMsg());
    } finally {
      setLoading(false);
    }
  };

  // Keyword search via Product Finder (/query) — respects all filters
  const discoverByKeyword = async () => {
    const term = keywordSearch.trim();
    if (!term) return;
    setLoading(true);
    setSearchError(null);
    try {
      // Build selection with filters, inject keyword as title filter
      // rootCategory 0 = all Amazon (no category restriction)
      const rootCat = catId ? parseInt(catId) : 0;
      const selection = buildSelection(rootCat);
      selection.title = term;
      // If no category, remove rootCategory so Finder searches all Amazon
      if (!catId) delete selection.rootCategory;

      const res = await api.post<any>('/publisher/discover/finder', {
        selection,
        seller_id: sellerId,
      });
      if (res.success && res.data) {
        const data = res.data;
        const newAsins = data.asins || data.new_asins || [];
        setAsins(newAsins);
        setSelected(new Set(newAsins));
        setTokens(data.tokens_left);
        setDiscovered(true);
        setInfo({
          total: newAsins.length,
          new: (data.new_asins || newAsins).length,
          dups: (data.duplicates || []).length,
          totalMatches: data.total_results,
          keyword: term,
          filtered: true,
        });
      } else {
        setSearchError(searchErrorMsg(res.error));
      }
    } catch (err) {
      console.error('Keyword search failed:', err);
      setSearchError(searchErrorMsg());
    } finally {
      setLoading(false);
    }
  };

  // V1 parity: if hasAdvFilters, both buttons use Filtered Search
  const handleDiscover = () => {
    if (hasAdvFilters) return discoverFiltered();
    return discoverBestsellers();
  };

  // Confirm: queue or curate
  const handleConfirm = async () => {
    if (selected.size === 0) return;
    if (!quickMode && onCurate) {
      onCurate(Array.from(selected));
      return;
    }
    setEnqueuing(true);
    try {
      await api.post('/publisher/discover/bestsellers/queue', {
        selected_asins: Array.from(selected),
        seller_id: sellerId,
        sites,
        condition,
      });
      setSelected(new Set());
      setDiscovered(false);
      setAsins([]);
    } catch (err) {
      console.error('Queue failed:', err);
    } finally {
      setEnqueuing(false);
    }
  };

  const toggle = (a: string) => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(a)) n.delete(a); else n.add(a);
    return n;
  });

  return (
    <div className="space-y-4">
      {/* Keyword search (no category required) */}
      <div>
        <label className="text-xs text-[var(--color-text-tertiary)] mb-1.5 block">Search Amazon by keyword</label>
        <div className="flex gap-2">
          <input
            value={keywordSearch}
            onChange={e => setKeywordSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && discoverByKeyword()}
            placeholder="e.g. stanley tumbler, wireless earbuds, yoga mat..."
            className="flex-1 px-3 py-1.5 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
          />
          <button
            onClick={discoverByKeyword}
            disabled={loading || !keywordSearch.trim()}
            className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 flex items-center gap-1.5"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            Search
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 text-[10px] text-[var(--color-text-tertiary)]">
        <div className="flex-1 h-px bg-[var(--color-border)]" />
        <span>or discover by category</span>
        <div className="flex-1 h-px bg-[var(--color-border)]" />
      </div>

      {/* Category search */}
      <div>
        <label className="text-xs text-[var(--color-text-tertiary)] mb-1.5 block">Search category by name</label>
        <div className="flex gap-2">
          <input
            value={catSearch}
            onChange={e => setCatSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && searchCategories()}
            placeholder="e.g. headphones, electronics, toys..."
            className="flex-1 px-3 py-1.5 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
          />
          <button
            onClick={searchCategories}
            disabled={catSearchLoading || !catSearch.trim()}
            className="px-4 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-xs font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-text)] disabled:opacity-50"
          >
            Search
          </button>
        </div>
        {catSearchResults !== null && (
          <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)]">
            {catSearchResults.length === 0 ? (
              <div className="px-3 py-2 text-xs text-[var(--color-text-tertiary)]">No categories found</div>
            ) : catSearchResults.map(cat => (
              <button
                key={cat.catId || cat.id}
                onClick={() => selectSearchCat(cat)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left border-b border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)] transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[var(--color-text)]">{cat.name}</div>
                  <div className="text-[10px] text-[var(--color-text-tertiary)]">
                    {(cat.productCount || 0).toLocaleString()} products
                    <span className="ml-1 opacity-60">ID: {cat.catId || cat.id}</span>
                  </div>
                </div>
                <span className="text-[10px] text-amber-500 font-mono flex-shrink-0">{cat.catId || cat.id}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Category ID input */}
      <div>
        <label className="text-xs text-[var(--color-text-tertiary)] mb-1.5 block">
          Amazon Category ID{catName ? ' \u2014 ' : ''}
          <span className="text-[var(--color-text)] font-semibold">{catName}</span>
        </label>
        <input
          value={catId}
          onChange={e => { setCatId(e.target.value); setDiscovered(false); }}
          placeholder="Keepa Category ID (use search above or Categories tab)"
          className="w-full px-3 py-1.5 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm font-mono text-[var(--color-text)]"
        />
      </div>

      {/* Two discover buttons (V1 parity) */}
      <div className="flex gap-2">
        <button
          onClick={handleDiscover}
          disabled={!catId || loading}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Fetches category bestsellers from Keepa. Does NOT apply filters."
        >
          {loading && !discovered ? <Loader2 size={14} className="animate-spin" /> : <Star size={14} />}
          {loading && !discovered ? 'Discovering...' : '\u{1F3C6} Top Bestsellers'}
        </button>
        <button
          onClick={discoverFiltered}
          disabled={!catId || loading}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Uses Keepa Product Finder with ALL filters applied (basic + advanced)."
        >
          {loading && !discovered ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          {loading && !discovered ? 'Searching...' : '\u{1F50D} Filtered Search'}
        </button>
      </div>

      {/* Filter status indicator */}
      {hasAdvFilters ? (
        <div className="text-[10px] text-center -mt-2 text-purple-400">
          Advanced filters active — both buttons use Filtered Search
        </div>
      ) : (
        <div className="text-[10px] text-center -mt-2 text-[var(--color-text-tertiary)]">
          <span className="text-amber-400">{'\u{1F3C6}'} Bestsellers</span>
          <span> = top sellers · </span>
          <span className="text-blue-400">{'\u{1F50D}'} Filtered</span>
          <span> = all filters</span>
        </div>
      )}

      {/* Search error (Keepa sin crédito, red, etc.) */}
      {searchError && (
        <div className="px-3 py-2 rounded-lg bg-red-900/20 border border-red-800/40 text-sm text-red-400">
          {searchError}
        </div>
      )}

      {/* Token badge */}
      {tokens != null && (
        <div className="flex justify-end">
          <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${tokens > 100 ? 'bg-green-900/20 text-green-400' : tokens > 20 ? 'bg-yellow-900/20 text-yellow-400' : 'bg-red-900/20 text-red-400'}`}>
            {tokens} tokens left
          </span>
        </div>
      )}

      {/* Results */}
      {discovered && (
        <div className="space-y-3">
          <div className="flex justify-between items-center text-xs">
            <div className="text-[var(--color-text-tertiary)]">
              {info?.filtered && info.totalMatches != null && (
                <span className="text-blue-400">{info.totalMatches.toLocaleString()} matches · </span>
              )}
              <strong className="text-[var(--color-text)]">{info?.total || 0}</strong> found
              {info?.dups > 0 && (
                <span className="text-[var(--color-text-tertiary)] ml-1">· {info.dups} already queued</span>
              )}
            </div>
          </div>

          {/* ASIN Grid (compact, like V1's FeederAsinGrid) */}
          {asins.length > 0 && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs text-[var(--color-text-tertiary)]">{selected.size}/{asins.length} selected</span>
                <div className="flex gap-3">
                  <button onClick={() => setSelected(new Set(asins))} className="text-xs text-[var(--color-primary)] hover:underline">All</button>
                  <button onClick={() => setSelected(new Set())} className="text-xs text-[var(--color-primary)] hover:underline">None</button>
                </div>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-1 max-h-60 overflow-y-auto p-0.5">
                {asins.map(a => {
                  const sel = selected.has(a);
                  return (
                    <button
                      key={a}
                      onClick={() => toggle(a)}
                      className={`px-2 py-1.5 rounded text-xs font-mono transition-all border ${
                        sel
                          ? 'bg-[var(--color-primary)]/15 border-[var(--color-primary)]/40 text-[var(--color-primary)]'
                          : 'bg-[var(--color-bg-tertiary)]/50 border-[var(--color-border)] text-[var(--color-text-tertiary)] hover:text-[var(--color-text)]'
                      }`}
                    >
                      {a}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Confirm button */}
          <div className="flex justify-end">
            <button
              onClick={handleConfirm}
              disabled={selected.size === 0 || enqueuing}
              className={`px-4 py-2 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                quickMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'
              }`}
            >
              {enqueuing ? 'Queueing...' : `${quickMode ? 'Queue' : 'Curate'} ${selected.size} Selected`}
            </button>
          </div>
        </div>
      )}

      {!discovered && !catId && !keywordSearch.trim() && (
        <div className="py-8 text-center text-[var(--color-text-tertiary)] text-xs">
          Search by keyword above, or select a category for Bestsellers / Filtered Search
        </div>
      )}
    </div>
  );
}

export { DiscoverSubTab };
